import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useParams, useSearchParams } from "react-router-dom";
import { FrameWithSearchBox } from "../common/Frame";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DATE_FORMAT, getDateFnsLocale } from "../date-utils";
import { addDays, compareDesc, format, formatISO, isValid, parse, parseISO, startOfDay, subMonths } from "date-fns";
import { useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import useAxios from "axios-hooks";
import Grid from "@mui/material/Grid";
import { DateRange } from "../common/DateRange";
import qs from "qs";
import { TableTemplate } from "../common/Table";
import { Box, Dialog, DialogContent, IconButton, TableCell, Typography } from "@mui/material";

import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import BlockIcon from '@mui/icons-material/Block';
import { MaterialList, SceneFormatted } from "../schemas";
import { Detail } from "./Detail";

const locale = getDateFnsLocale();

export function Overview() {

  const { materialId } = useParams();

  const [searchParam, setSearchParams] = useSearchParams();

  const paramFrom = parse(searchParam.get('from') || '', DATE_FORMAT, new Date());
  const paramTo = parse(searchParam.get('to') || '', DATE_FORMAT, new Date());

  const today = startOfDay(new Date());
  const initFrom = isValid(paramFrom) ? paramFrom : subMonths(addDays(today, 1), 1);
  const initTo = isValid(paramTo) ? paramTo : today;

  const [from, setFrom] = useState<Date | null>(initFrom);
  const [to, setTo] = useState<Date | null>(initTo);

  const [detailId, setDetailId] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { user } = useAuthenticator();
  const token = user?.getSignInUserSession()?.getIdToken().getJwtToken();

  const url = `${process.env.REACT_APP_AWS_API_GATEWAY_URL}/scene`;

  const [{ data, loading, error }, refetch] = useAxios<MaterialList>({
    url,
    headers: { Authorization: token },
    params: {
      'material-id': materialId,
      from: formatISO(initFrom),
      to: formatISO(addDays(initTo, 1)),
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }
  });

  const onSearchClicked = () => {
    if (!isValid(from) || !isValid(to)) return;

    setSearchParams(qs.stringify({
      from: from ? format(from, DATE_FORMAT) : undefined,
      to: to ? format(to, DATE_FORMAT) : undefined,
    }),
      { replace: true }
    )
    setPage(0);
    setRowsPerPage(10);
  }

  const onResetClicked = () => {
    setSearchParams("", { replace: true })
    setFrom(today);
    setTo(today);
  }

  const scenes = data?.items
    .flatMap(i => i.scenes.map(s => ({ id: s.scene_id, name: s.scene_name })))
    .filter((value, i, array) => {
      for (var j = i + 1; j < array.length; j++) {
        if (value.id === array[j].id) return false;
      }
      return true;
    }) || []
  scenes?.sort((a, b) => a.name.localeCompare(b.name));

  const tableData = Object.fromEntries(data?.items
    .map(i => [
      i.id,
      i.scenes.reduce((m, s) => {
        if (!(s.scene_id in m)) m[s.scene_id] = [];
        const numerator = s.clicked_area_count + s.correct_question_count;
        const denominator = s.total_area_count + s.total_question_count;

        m[s.scene_id].push({
          numerator,
          denominator,
          end_type: s.end_type,
          start_datetime: parseISO(s.start_datetime),
          end_datetime: parseISO(s.end_datetime),
        })
        return m
      }, {} as { [key: string]: SceneFormatted[] })
    ]) || []
  )

  const detailData = data && detailId ? data.items.find((d) => d.id === detailId) || null : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <FrameWithSearchBox
        title={`Overview: ${data?.material_name || ''}`}
        onSearchClicked={onSearchClicked}
        onResetClicked={onResetClicked}
      >
        <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
          <DateRange from={from} setFrom={setFrom} to={to} setTo={setTo} required />
        </Grid>
        <TableTemplate
          columns={[
            { name: '' },
            { name: 'Datetime' },
            { name: 'Executed by' },
            ...scenes.map(s => ({ name: s.name, props: { sx: { textAlign: 'center', minWidth: 120 } } }))
          ]}
          dataRows={data?.items}
          count={data?.count}
          rowRenderFunc={row => [
            <TableCell key='detail' sx={{ p: 0.5 }}>
              <IconButton aria-label="delete" size="small" onClick={() => setDetailId(row.id)}>
                <ManageSearchIcon fontSize="inherit" />
              </IconButton>
            </TableCell>,
            <TableCell key='date'>
              {format(parseISO(row.datetime), 'PPpp', { locale })}
            </TableCell>,
            <TableCell key='exe'>
              {row.experiencer_name ||
                <Typography variant="inherit" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>(no name)</Typography>
              }
            </TableCell>,
            ...scenes.map(s => {
              const d = (tableData[row.id] || {})[s.id];
              d && d.sort((a, b) => compareDesc(a.end_datetime, b.end_datetime))

              return d
                ?
                <TableCell key={`c${s.id}`}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="inherit" sx={{ maxWidth: 50, width: 'calc(40% - 7px)', textAlign: 'right' }}>
                      {d[0].numerator}
                    </Typography>
                    <Typography variant="inherit" sx={{ width: 14, textAlign: 'center' }}>
                      /
                    </Typography>
                    <Typography variant="inherit" sx={{ maxWidth: 50, width: 'calc(30% - 7px)', textAlign: 'left' }}>
                      {d[0].denominator}
                    </Typography>
                    <Typography variant="inherit" sx={{ maxWidth: 30, width: '30%', display: 'flex', alignItems: 'center' }}>
                      {d.length > 1 && `(${d.length})`}
                    </Typography>
                  </Box>
                </TableCell>
                :
                <TableCell key={`c${s.id}`} sx={{ textAlign: 'center' }}>&mdash;</TableCell>
            })
          ]}
          loading={loading}
          error={error}
          page={page}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </FrameWithSearchBox>
      <Dialog
        open={!!detailId && !!detailData}
        onClose={() => setDetailId(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          {detailData && data && <Detail name={data?.material_name} data={detailData} />}
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  )
}

const EndTypeIcon = (props: { endType: number }) => {

  switch (props.endType) {
    case 1:
      return <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />
    case 9:
      return <BlockIcon sx={{ fontSize: 15 }} />
  }

  return <QuestionMarkIcon sx={{ fontSize: 15 }} />
}
